import { SupportedLanguage } from './pricingTranslations';

interface SubjectHighlight {
  title: string;
  helper: string;
  value?: string;
}

interface DialogText {
  addTitle: string;
  selectSubjectLabel: string;
  chooseFromList: string;
  otherOption: string;
  customSubjectLabel: string;
  customSubjectPlaceholder: string;
  subjectsInfo: string;
  cancel: string;
  saveSubject: string;
  removeTitle: string;
  removeDescriptionPart1: string;
  removeDescriptionPart2: string;
  keep: string;
  remove: string;
}

interface SnackbarMessages {
  addSuccess: (subject: string) => string;
  addFail: string;
  removeInfo: (subject: string) => string;
  loadFail: string;
  duplicateError: string;
  selectError: string;
}

interface AiInsightsContent {
  title: string;
  subheader: string;
  priorityLabel: string;
  priorityValue: string;
  priorityDescription: string;
  nextLabel: string;
  nextValue: string;
  nextDescription: string;
}

interface TokenUsageWidgetCopy {
  title: string;
  helper: string;
  limitLabel: string;
  tokenLabel: string;
  loadError: string;
  dialogTitle: string;
  dialogDescription: string;
  dialogButton: string;
}

interface DigitalWellbeingCopy {
  highlightTitle: string;
  highlightHelper: string;
  dialogTitle: string;
  dialogDescription: string;
  totalLabel: string;
  activeDaysLabel: string;
  streakLabel: string;
  streakValue: (days: number) => string;
  chartDatasetLabel: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  loadError: string;
  sessionCountLabel: (count: number) => string;
  noDataLabel: string;
  closeButton: string;
}

interface StudentDashboardTranslation {
  welcome: string;
  missionControl: string;
  missionDescription: string;
  jumpIntoMock: string;
  addNewSubject: string;
  subjectHighlights: SubjectHighlight[];
  weeklyMockValue: string;
  nextRevisionValue: string;
  mySubjectsTitle: string;
  mySubjectsSubheader: string;
  addSubjectButton: string;
  letsBuildCurriculumTitle: string;
  letsBuildCurriculumDesc: string;
  aiInsights: AiInsightsContent;
  aiStudyNavigatorTitle: string;
  aiStudyNavigatorSubheader: string;
  adaptiveMockSuggestionTitle: string;
  adaptiveMockSuggestionDesc: string;
  launchMockStudio: string;
  dialogText: DialogText;
  snackbarMessages: SnackbarMessages;
  digitalWellbeing: DigitalWellbeingCopy;
  tokenUsageWidget?: TokenUsageWidgetCopy;
}

export const studentDashboardTranslations: Record<SupportedLanguage, StudentDashboardTranslation> = {
  en: {
    welcome: 'Welcome back',
    missionControl: 'Your AI learning mission control',
    missionDescription:
      'Pick up exactly where you left off, review AI insights on your progress, and launch the next action with a click. Everything is orchestrated around your exam goals.',
    jumpIntoMock: 'Jump into mock studio',
    addNewSubject: 'Add new subject',
    subjectHighlights: [
      { title: 'Active subjects', helper: 'Curated learning plans running this term' },
      { title: 'Study Streak', helper: 'Keep the momentum going!', value: '0 days' },
      { title: 'Next revision window', helper: '45 min deep dive into problem areas', value: 'Thursday, 6 PM' },
    ],
    weeklyMockValue: '3 mocks',
    nextRevisionValue: 'Thursday, 6 PM',
    mySubjectsTitle: 'My subjects',
    mySubjectsSubheader: 'Tap a subject to open the collaborative workspace',
    addSubjectButton: 'Add subject',
    letsBuildCurriculumTitle: "Let's build your personalised curriculum",
    letsBuildCurriculumDesc:
      'Add your first subject to unlock AI coaching, mock exams, and tailored revision plans.',
    aiInsights: {
      title: 'AI insights',
      subheader: 'Real-time view from the tutor engine',
      priorityLabel: 'Priority focus',
      priorityValue: 'Concept application drills',
      priorityDescription: 'Spend 30 minutes today reviewing the toughest 20% of past mock responses.',
      nextLabel: 'Next best action',
      nextValue: 'Schedule a revision sprint',
      nextDescription: 'AI planner suggests a 45-minute sprint on algebra and essay structure tomorrow.',
    },
    aiStudyNavigatorTitle: 'AI study navigator',
    aiStudyNavigatorSubheader: 'Personalised actions for today',
    adaptiveMockSuggestionTitle: 'Adaptive mock suggestion',
    adaptiveMockSuggestionDesc:
      'Run a mixed-topic mock focusing on algebraic proofs & essay structure.',
    launchMockStudio: 'Launch mock studio',
    dialogText: {
      addTitle: 'Add a new subject',
      selectSubjectLabel: 'Select subject',
      chooseFromList: 'Choose from list',
      otherOption: 'Other (custom)',
      customSubjectLabel: 'Custom subject name',
      customSubjectPlaceholder: 'E.g. Further Mathematics',
      subjectsInfo:
        'Subjects unlock smart chat, AI-generated mocks, and guided revision flows tailored to your curriculum.',
      cancel: 'Cancel',
      saveSubject: 'Save subject',
      removeTitle: 'Remove subject',
      removeDescriptionPart1: 'This will remove',
      removeDescriptionPart2:
        'from your workspace. You can add it back at any time, but AI recommendations will reset.',
      keep: 'Keep subject',
      remove: 'Remove',
    },
    snackbarMessages: {
      addSuccess: (subject) => `${subject} added to your workspace`,
      addFail: 'Failed to add subject. Please try again.',
      removeInfo: (subject) => `${subject} removed from your list`,
      loadFail: 'Failed to load subjects. Please try again.',
      duplicateError: 'This subject already exists.',
      selectError: 'Please select or enter a subject.',
    },

    digitalWellbeing: {
      highlightTitle: 'Digital wellbeing',
      highlightHelper: 'Track how long you spent on Zaptick today',
      dialogTitle: 'Digital wellbeing snapshot',
      dialogDescription:
        'We automatically log active browser sessions so you can share progress with parents or mentors.',
      totalLabel: 'Total tracked time',
      activeDaysLabel: 'Active days this week',
      streakLabel: 'Current streak',
      streakValue: (days) => `${days} day${days === 1 ? '' : 's'}`,
      chartDatasetLabel: 'Hours spent on Zaptick',
      emptyStateTitle: 'No activity yet',
      emptyStateDescription:
        'Spend time inside the learning workspace to see usage insights appear here.',
      loadError: 'Unable to load usage insights. Please try again.',
      sessionCountLabel: (count) => `${count} session${count === 1 ? '' : 's'}`,
      noDataLabel: 'No activity yet',
      closeButton: 'Close',
    },
    tokenUsageWidget: {
      title: 'AI credits',
      helper: 'Free credits remaining',
      limitLabel: 'Free credit cap',
      tokenLabel: 'credits',
      loadError: 'Unable to load credit usage. Please try again later.',
      dialogTitle: 'Credits exhausted',
      dialogDescription:
        'You have used all free AI credits. Please subscribe to continue using premium features.',
      dialogButton: 'Close',
    },
  },
  zh: {
    welcome: '欢迎回来',
    missionControl: '您的 AI 学习指挥中心',
    missionDescription:
      '准确衔接上一次学习进度，查看 AI 对您进展的洞察，并一键开启下一步任务，一切都围绕考试目标展开。',
    jumpIntoMock: '进入模拟考工作室',
    addNewSubject: '添加新科目',
    subjectHighlights: [
      { title: '当前科目', helper: '本学期正在运行的学习计划' },
      { title: '学习连胜', helper: '保持势头！', value: '0 天' },
      { title: '下一次复习时间', helper: '45 分钟聚焦于薄弱环节', value: '周四，下午 6 点' },
    ],
    weeklyMockValue: '3 场模拟',
    nextRevisionValue: '周四，下午 6 点',
    mySubjectsTitle: '我的科目',
    mySubjectsSubheader: '点击科目即可打开协作空间',
    addSubjectButton: '添加科目',
    letsBuildCurriculumTitle: '让我们定制你的专属课程表',
    letsBuildCurriculumDesc: '添加第一个科目即可解锁 AI 辅导、模拟考试与专属复习计划。',
    aiInsights: {
      title: 'AI 洞察',
      subheader: '来自导师引擎的实时视图',
      priorityLabel: '优先任务',
      priorityValue: '概念应用训练',
      priorityDescription: '今天花 30 分钟复盘最具挑战性的 20% 模拟题答案。',
      nextLabel: '下一步行动',
      nextValue: '安排复习冲刺',
      nextDescription: 'AI 建议明天进行 45 分钟的代数与写作结构冲刺训练。',
    },
    aiStudyNavigatorTitle: 'AI 学习导航',
    aiStudyNavigatorSubheader: '今日专属行动',
    adaptiveMockSuggestionTitle: '自适应模拟建议',
    adaptiveMockSuggestionDesc: '进行一次聚焦于代数证明与作文结构的混合主题模拟。',
    launchMockStudio: '开启模拟工作室',
    dialogText: {
      addTitle: '添加新科目',
      selectSubjectLabel: '选择科目',
      chooseFromList: '从列表中选择',
      otherOption: '自定义科目',
      customSubjectLabel: '自定义科目名称',
      customSubjectPlaceholder: '例如：高等数学',
      subjectsInfo:
        '添加科目即可解锁智能聊天、AI 生成的模拟题以及量身定制的复习流程。',
      cancel: '取消',
      saveSubject: '保存科目',
      removeTitle: '移除科目',
      removeDescriptionPart1: '这将把',
      removeDescriptionPart2: '从你的工作区移除。你可以随时重新添加，但 AI 建议将重新开始。',
      keep: '保留科目',
      remove: '移除',
    },
    snackbarMessages: {
      addSuccess: (subject) => `已将 ${subject} 添加到你的工作区`,
      addFail: '添加科目失败，请重试。',
      removeInfo: (subject) => `已将 ${subject} 从列表中移除`,
      loadFail: '加载科目失败，请重试。',
      duplicateError: '该科目已存在。',
      selectError: '请选择或输入一个科目。',
    },

    digitalWellbeing: {
      highlightTitle: 'Digital wellbeing',
      highlightHelper: 'Track how long you spent on Zaptick today',
      dialogTitle: 'Digital wellbeing snapshot',
      dialogDescription:
        'We automatically log active browser sessions so you can share progress with parents or mentors.',
      totalLabel: 'Total tracked time',
      activeDaysLabel: 'Active days this week',
      streakLabel: 'Current streak',
      streakValue: (days) => `${days} day${days === 1 ? '' : 's'}`,
      chartDatasetLabel: 'Hours spent on Zaptick',
      emptyStateTitle: 'No activity yet',
      emptyStateDescription:
        'Spend time inside the learning workspace to see usage insights appear here.',
      loadError: 'Unable to load usage insights. Please try again.',
      sessionCountLabel: (count) => `${count} session${count === 1 ? '' : 's'}`,
      noDataLabel: 'No activity yet',
      closeButton: 'Close',
    },
  },
  ko: {
    welcome: '다시 만나 반가워요',
    missionControl: 'AI 학습 미션 컨트롤',
    missionDescription:
      '이전에 멈춘 지점부터 바로 이어서 학습하고, AI 인사이트로 진행 상황을 확인하며, 클릭 한 번으로 다음 액션을 실행하세요. 모든 것이 시험 목표에 맞춰 조율됩니다.',
    jumpIntoMock: '모의 스튜디오로 이동',
    addNewSubject: '새 과목 추가',
    subjectHighlights: [
      { title: '활성 과목', helper: '이번 학기에 진행 중인 학습 계획' },
      { title: '학습 스트릭', helper: '페이스를 유지하세요!', value: '0일' },
      { title: '다음 복습 시간', helper: '취약 영역을 45분 집중 학습', value: '목요일 오후 6시' },
    ],
    weeklyMockValue: '모의 3회',
    nextRevisionValue: '목요일 오후 6시',
    mySubjectsTitle: '내 과목',
    mySubjectsSubheader: '과목을 눌러 협업 공간을 열어보세요',
    addSubjectButton: '과목 추가',
    letsBuildCurriculumTitle: '맞춤형 커리큘럼을 함께 만들어볼까요?',
    letsBuildCurriculumDesc: '첫 과목을 추가하면 AI 코칭, 모의고사, 맞춤 복습 플랜이 열립니다.',
    aiInsights: {
      title: 'AI 인사이트',
      subheader: '튜터 엔진의 실시간 뷰',
      priorityLabel: '우선 집중 영역',
      priorityValue: '개념 적용 드릴',
      priorityDescription: '오늘 30분 동안 지난 모의 응답 중 가장 어려웠던 20%를 복습하세요.',
      nextLabel: '다음 추천 액션',
      nextValue: '복습 스프린트 예약',
      nextDescription: 'AI가 내일 45분 알gebra·에세이 구조 스프린트를 추천합니다.',
    },
    aiStudyNavigatorTitle: 'AI 학습 내비게이터',
    aiStudyNavigatorSubheader: '오늘의 맞춤 액션',
    adaptiveMockSuggestionTitle: '적응형 모의 제안',
    adaptiveMockSuggestionDesc: '대수 증명과 에세이 구조에 집중한 혼합 주제 모의를 실행하세요.',
    launchMockStudio: '모의 스튜디오 실행',
    dialogText: {
      addTitle: '새 과목 추가',
      selectSubjectLabel: '과목 선택',
      chooseFromList: '목록에서 선택',
      otherOption: '기타 (직접 입력)',
      customSubjectLabel: '직접 입력 과목',
      customSubjectPlaceholder: '예: 심화 수학',
      subjectsInfo:
        '과목을 추가하면 스마트 챗, AI 생성 모의고사, 맞춤형 복습 플로우가 열립니다.',
      cancel: '취소',
      saveSubject: '과목 저장',
      removeTitle: '과목 삭제',
      removeDescriptionPart1: '이 작업은',
      removeDescriptionPart2:
        '을(를) workspace에서 제거합니다. 언제든 다시 추가할 수 있지만 AI 추천은 초기화됩니다.',
      keep: '유지',
      remove: '삭제',
    },
    snackbarMessages: {
      addSuccess: (subject) => `${subject} 과목이 워크스페이스에 추가되었습니다`,
      addFail: '과목 추가에 실패했습니다. 다시 시도하세요.',
      removeInfo: (subject) => `${subject} 과목을 목록에서 삭제했습니다`,
      loadFail: '과목을 불러오지 못했습니다. 다시 시도하세요.',
      duplicateError: '이미 존재하는 과목입니다.',
      selectError: '과목을 선택하거나 입력하세요.',
    },

    digitalWellbeing: {
      highlightTitle: 'Digital wellbeing',
      highlightHelper: 'Track how long you spent on Zaptick today',
      dialogTitle: 'Digital wellbeing snapshot',
      dialogDescription:
        'We automatically log active browser sessions so you can share progress with parents or mentors.',
      totalLabel: 'Total tracked time',
      activeDaysLabel: 'Active days this week',
      streakLabel: 'Current streak',
      streakValue: (days) => `${days} day${days === 1 ? '' : 's'}`,
      chartDatasetLabel: 'Hours spent on Zaptick',
      emptyStateTitle: 'No activity yet',
      emptyStateDescription:
        'Spend time inside the learning workspace to see usage insights appear here.',
      loadError: 'Unable to load usage insights. Please try again.',
      sessionCountLabel: (count) => `${count} session${count === 1 ? '' : 's'}`,
      noDataLabel: 'No activity yet',
      closeButton: 'Close',
    },
  },
  ar: {
    welcome: 'مرحباً بعودتك',
    missionControl: 'مركز التحكم بتعلّمك المدعوم بالذكاء الاصطناعي',
    missionDescription:
      'تابع من حيث توقفت تماماً، واطّلع على تحليلات الذكاء الاصطناعي لتقدّمك، وابدأ الخطوة التالية بنقرة واحدة. كل شيء مُصمم لخدمة أهدافك الامتحانية.',
    jumpIntoMock: 'انتقل إلى استوديو المحاكاة',
    addNewSubject: 'إضافة مادة جديدة',
    subjectHighlights: [
      { title: 'المواد النشطة', helper: 'خطط تعلّم منسّقة لهذا الفصل' },
      { title: 'سلسلة الدراسة', helper: 'حافظ على الزخم!', value: '0 أيام' },
      { title: 'نافذة المراجعة التالية', helper: '45 دقيقة تركيز على نقاط الضعف', value: 'الخميس، 6 مساءً' },
    ],
    weeklyMockValue: '3 اختبارات',
    nextRevisionValue: 'الخميس، 6 مساءً',
    mySubjectsTitle: 'موادي',
    mySubjectsSubheader: 'اضغط على مادة لفتح مساحة العمل التعاونية',
    addSubjectButton: 'أضف مادة',
    letsBuildCurriculumTitle: 'لنُنشئ منهجك المخصص',
    letsBuildCurriculumDesc:
      'أضف مادتك الأولى لتفتح التدريب الذكي، الاختبارات التجريبية، وخطط المراجعة المصممة لك.',
    aiInsights: {
      title: 'رؤى الذكاء الاصطناعي',
      subheader: 'عرض لحظي من محرك المُعلّم',
      priorityLabel: 'أولوية التركيز',
      priorityValue: 'تدريبات تطبيق المفاهيم',
      priorityDescription: 'خصّص 30 دقيقة اليوم لمراجعة أصعب 20٪ من إجاباتك التجريبية السابقة.',
      nextLabel: 'أفضل خطوة تالية',
      nextValue: 'جدولة جولة مراجعة',
      nextDescription: 'يوصي الذكاء الاصطناعي بجلسة مراجعة لمدة 45 دقيقة للجبرو الرسم غداً.',
    },
    aiStudyNavigatorTitle: 'موجّه الدراسة الذكي',
    aiStudyNavigatorSubheader: 'إجراءات مخصصة لليوم',
    adaptiveMockSuggestionTitle: 'اقتراح محاكاة تكيفي',
    adaptiveMockSuggestionDesc:
      'أجْر اختباراً مختلط المواضيع يركّز على براهين الجبر وبنية المقال.',
    launchMockStudio: 'ابدأ استوديو المحاكاة',
    dialogText: {
      addTitle: 'إضافة مادة جديدة',
      selectSubjectLabel: 'اختر المادة',
      chooseFromList: 'اختر من القائمة',
      otherOption: 'أخرى (مخصصة)',
      customSubjectLabel: 'اسم مادة مخصص',
      customSubjectPlaceholder: 'مثال: رياضيات متقدمة',
      subjectsInfo:
        'إضافة المواد تفتح المحادثة الذكية، المحاكاة المولدة بالذكاء الاصطناعي، ومسارات المراجعة الموجهة.',
      cancel: 'إلغاء',
      saveSubject: 'حفظ المادة',
      removeTitle: 'إزالة المادة',
      removeDescriptionPart1: 'سيتم إزالة',
      removeDescriptionPart2:
        'من مساحة عملك. يمكنك إضافتها مجدداً في أي وقت، لكن توصيات الذكاء الاصطناعي ستُعاد ضبطها.',
      keep: 'الاحتفاظ بالمادة',
      remove: 'إزالة',
    },
    snackbarMessages: {
      addSuccess: (subject) => `تمت إضافة ${subject} إلى مساحة عملك`,
      addFail: 'تعذّر إضافة المادة. يرجى المحاولة مجدداً.',
      removeInfo: (subject) => `تمت إزالة ${subject} من قائمتك`,
      loadFail: 'تعذّر تحميل المواد. يرجى المحاولة مجدداً.',
      duplicateError: 'هذه المادة موجودة بالفعل.',
      selectError: 'يرجى اختيار مادة أو إدخالها.',
    },

    digitalWellbeing: {
      highlightTitle: 'Digital wellbeing',
      highlightHelper: 'Track how long you spent on Zaptick today',
      dialogTitle: 'Digital wellbeing snapshot',
      dialogDescription:
        'We automatically log active browser sessions so you can share progress with parents or mentors.',
      totalLabel: 'Total tracked time',
      activeDaysLabel: 'Active days this week',
      streakLabel: 'Current streak',
      streakValue: (days) => `${days} day${days === 1 ? '' : 's'}`,
      chartDatasetLabel: 'Hours spent on Zaptick',
      emptyStateTitle: 'No activity yet',
      emptyStateDescription:
        'Spend time inside the learning workspace to see usage insights appear here.',
      loadError: 'Unable to load usage insights. Please try again.',
      sessionCountLabel: (count) => `${count} session${count === 1 ? '' : 's'}`,
      noDataLabel: 'No activity yet',
      closeButton: 'Close',
    },
  },
};
